import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import Select from "react-select";
import tinycolor from "tinycolor2";
import EmptyState from '../components/CommonComponents/EmptyState';
import { BarChart3, ShoppingCart, AlertCircle, Clock, Wallet } from 'lucide-react';
import { useTheme } from "../components/CommonComponents/ThemeProvider";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend);

const API_URL = `${process.env.REACT_APP_API_URL}/api/admin/orders/analytics`;

// add a constant for low-stock threshold
const LOW_STOCK_THRESHOLD = parseInt(process.env.REACT_APP_LOW_STOCK_THRESHOLD) || 10;

const createBranchSelectStyles = (themeColor) => {
  const hoverTint = tinycolor(themeColor).setAlpha(0.08).toRgbString();

  return {
    control: (base, state) => ({
      ...base,
      minHeight: 40,
      borderColor: state.isFocused ? themeColor : "#e5e7eb",
      boxShadow: state.isFocused ? `0 0 0 1px ${themeColor}` : "none",
      borderRadius: 8,
      "&:hover": { borderColor: themeColor },
      fontSize: 14,
    }),
    valueContainer: (base) => ({
      ...base,
      padding: "0 8px",
    }),
    menu: (base) => ({
      ...base,
      borderRadius: 8,
      border: "1px solid #e5e7eb",
      overflow: "hidden",
      zIndex: 30,
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? themeColor
        : state.isFocused
          ? hoverTint
          : "white",
      color: state.isSelected ? "#fff" : "#111827",
      fontSize: 14,
    }),
    singleValue: (base) => ({
      ...base,
      color: "#111827",
    }),
    placeholder: (base) => ({
      ...base,
      color: "#6b7280",
    }),
    indicatorSeparator: () => ({ display: "none" }),
    dropdownIndicator: (base, state) => ({
      ...base,
      color: state.isFocused ? themeColor : "#6b7280",
      "&:hover": {
        color: themeColor,
      },
    }),
  };
};

// Utility function to format currency
const formatCurrency = (amount) => {
  if (isNaN(amount)) return "₱0.00";
  return new Intl.NumberFormat("en-PH", {
    style: "currency",
    currency: "PHP",
    minimumFractionDigits: 2,
  }).format(amount);
};

const DashboardSkeleton = () => (
  <div className="min-h-screen bg-gray-50 px-0 py-4 animate-pulse sm:p-6">
    <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
      <div className="h-10 w-52 rounded-2xl bg-gradient-to-r from-slate-200 via-slate-100 to-slate-200" />
      <div className="w-full max-w-sm">
        <div className="mb-2 h-4 w-16 rounded-full bg-slate-200" />
        <div className="h-10 rounded-xl bg-gradient-to-r from-slate-200 via-slate-100 to-slate-200" />
      </div>
    </div>

    <div className="mb-8 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
      {["blue", "green", "amber", "red"].map((tone, idx) => (
        <div key={idx} className={`rounded-lg border-l-4 bg-white p-4 shadow ${tone === "blue" ? "border-blue-500" : tone === "green" ? "border-green-500" : tone === "amber" ? "border-amber-500" : "border-red-500"}`}>
          <div className="flex items-center justify-between">
            <div className="space-y-3">
              <div className="h-3 w-24 rounded-full bg-slate-200" />
              <div className="h-7 w-28 rounded-full bg-slate-300" />
              <div className="h-2.5 w-20 rounded-full bg-slate-100" />
            </div>
            <div className="h-10 w-10 rounded-2xl bg-slate-100" />
          </div>
        </div>
      ))}
    </div>

    <div className="mb-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
      <div className="rounded-lg bg-white p-6 shadow lg:col-span-2">
        <div className="mb-4 flex items-center justify-between">
          <div className="h-5 w-44 rounded-full bg-slate-200" />
          <div className="h-3 w-16 rounded-full bg-slate-100" />
        </div>
        <div className="h-60 rounded-2xl bg-gradient-to-br from-slate-100 via-white to-slate-100 p-4">
          <div className="flex h-full items-end gap-3">
            {[55, 72, 46, 88, 58, 70, 52].map((height, idx) => (
              <div key={idx} className="flex flex-1 items-end gap-2">
                <div
                  className="w-full rounded-t-2xl bg-gradient-to-t from-indigo-200 via-indigo-100 to-slate-100"
                  style={{ height: `${height}%` }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="rounded-lg bg-white p-6 shadow">
        <div className="mb-4 flex items-center justify-between">
          <div className="h-5 w-28 rounded-full bg-slate-200" />
          <div className="h-3 w-14 rounded-full bg-slate-100" />
        </div>
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, idx) => (
            <div key={idx} className="flex items-center justify-between border-b pb-3">
              <div className="flex-1 space-y-2">
                <div className="h-3.5 w-4/5 rounded-full bg-slate-200" />
                <div className="h-2.5 w-2/5 rounded-full bg-slate-100" />
              </div>
              <div className="ml-4 h-4 w-6 rounded-full bg-slate-200" />
            </div>
          ))}
        </div>
      </div>
    </div>

    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      {[0, 1].map((sectionIdx) => (
        <div key={sectionIdx} className="rounded-lg bg-white p-6 shadow">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="h-5 w-5 rounded-full bg-slate-200" />
              <div className="h-5 w-40 rounded-full bg-slate-200" />
            </div>
            <div className="h-3 w-14 rounded-full bg-slate-100" />
          </div>
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, idx) => (
              <div key={idx} className="flex items-center justify-between border-b pb-3 last:border-0">
                <div className="flex-1 space-y-2">
                  <div className="h-3.5 w-3/5 rounded-full bg-slate-200" />
                  <div className="h-2.5 w-1/3 rounded-full bg-slate-100" />
                </div>
                <div className="ml-4 h-6 w-16 rounded-full bg-slate-100" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

export default function Dashboard() {
  const { currentTheme } = useTheme();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [preReleaseRequests, setPreReleaseRequests] = useState([]);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [totalLowStockCount, setTotalLowStockCount] = useState(0);
  const [todayExpenseTotal, setTodayExpenseTotal] = useState(0);
  const [todayExpenseCount, setTodayExpenseCount] = useState(0);
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState("");
  const branchSelectStyles = createBranchSelectStyles(currentTheme.color);

  const branchOptions = [
    { value: "", label: "All Branches" },
    ...branches.map((branch) => ({
      value: String(branch.id),
      label: branch.name || branch.location || `Branch ${branch.id}`,
    })),
  ];

  // Fetch analytics data
  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      const customerId = sessionStorage.getItem("customer_id") || localStorage.getItem("customer_id");

      let url = API_URL;
      const params = new URLSearchParams();
      if (customerId) params.set("customer_id", customerId);
      if (selectedBranchId) params.set("branch_id", selectedBranchId);
      const queryString = params.toString();
      if (queryString) url += `?${queryString}`;

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      setAnalytics(data);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [selectedBranchId]);

  // Fetch pre-release requests
  const fetchPreReleases = useCallback(async () => {
    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      const params = new URLSearchParams({
        page: "1",
        pageSize: "3",
        status: "PENDING",
      });
      if (selectedBranchId) params.set("branch_id", selectedBranchId);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/pre-releases?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setPreReleaseRequests(data.data || []);
    } catch (err) {
      console.error("Failed to fetch pre-releases:", err);
    }
  }, [selectedBranchId]);

  // Fetch low stock items
  const fetchLowStock = useCallback(async () => {
    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      const params = new URLSearchParams({
        threshold: String(LOW_STOCK_THRESHOLD + 1),
      });
      if (selectedBranchId) params.set("branch_id", selectedBranchId);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/inventory/low-stock?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      const allLow = Array.isArray(data) ? data : [];
      setTotalLowStockCount(allLow.length);
      setLowStockItems(allLow.slice(0, 5));
    } catch (err) {
      console.error("Failed to fetch low stock:", err);
      setTotalLowStockCount(0);
      setLowStockItems([]);
    }
  }, [selectedBranchId]);

  // Fetch today's expenses total for admin dashboard card
  const fetchTodayExpenses = useCallback(async () => {
    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      const todayLocal = new Date().toLocaleDateString('en-CA');
      const params = new URLSearchParams({
        startDate: todayLocal,
        endDate: todayLocal,
      });
      if (selectedBranchId) params.set("branch_id", selectedBranchId);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/pos/expenses?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = await response.json();
      const expenseList = Array.isArray(data) ? data : [];
      const total = expenseList.reduce((sum, item) => sum + Number(item.amount || 0), 0);
      setTodayExpenseTotal(total);
      setTodayExpenseCount(expenseList.length);
    } catch (err) {
      console.error("Failed to fetch today's expenses:", err);
      setTodayExpenseTotal(0);
      setTodayExpenseCount(0);
    }
  }, [selectedBranchId]);

  const fetchDashboardBranches = useCallback(async () => {
    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      const customerId = sessionStorage.getItem("customer_id") || localStorage.getItem("customer_id");
      const params = new URLSearchParams();
      if (customerId) params.set("customer_id", customerId);
      const queryString = params.toString();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/branches${queryString ? `?${queryString}` : ""}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = await response.json();
      const branchList = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
      setBranches(
        branchList.filter((branch) => String(branch?.status || "").toUpperCase() === "ACTIVE")
      );
    } catch (err) {
      console.error("Failed to fetch branches:", err);
      setBranches([]);
    }
  }, []);

  useEffect(() => {
    fetchDashboardBranches();
    fetchAnalytics();
    fetchPreReleases();
    fetchLowStock();
    fetchTodayExpenses();
  }, [fetchDashboardBranches, fetchAnalytics, fetchPreReleases, fetchLowStock, fetchTodayExpenses]);

  if (error) {
    return (
      <div className="p-6">
        <EmptyState
          icon={BarChart3}
          title="Failed to Load Dashboard"
          message="Check your internet connection and try again."
          onRetry={fetchAnalytics}
          retryLabel="Retry"
        />
      </div>
    );
  }

  if (loading) {
    return <DashboardSkeleton />;
  }

  // Calculate metrics
  // Find today's date in YYYY-MM-DD format
  // Always match today's sales by local date string
  const todayDateLocal = new Date().toLocaleDateString('en-CA').trim();
  let today = { totalSales: 0, ordersCount: 0 };
  if (analytics?.salesByDay?.length) {
    const found = analytics.salesByDay.find(d => {
      const dt = new Date(`${d.date}T00:00:00Z`);
      return dt.toLocaleDateString('en-CA') === todayDateLocal;
    });
    if (found) today = found;
  }

  // Helper to filter by date range
  // Filter by date range using local time zone
  const filterByDateRange = (data, days) => {
    if (!Array.isArray(data)) return [];
    const today = new Date();
    return data.filter(d => {
      const dt = new Date(`${d.date}T00:00:00Z`);
      const localDate = new Date(dt.getTime() + dt.getTimezoneOffset() * 60000 * -1);
      const diff = (today - localDate) / (1000 * 60 * 60 * 24);
      return diff >= 0 && diff < days;
    });
  };

  // This month: filter by current month
  const now = new Date();
  const thisMonth = (analytics?.salesByDay || []).filter(d => {
    const dt = new Date(`${d.date}T00:00:00Z`);
    const localDate = new Date(dt.getTime() + dt.getTimezoneOffset() * 60000 * -1);
    return localDate.getMonth() === now.getMonth() && localDate.getFullYear() === now.getFullYear();
  });
  const totalSalesMonth = thisMonth.reduce((sum, d) => sum + parseFloat(d.totalSales), 0);
  const totalOrdersMonth = thisMonth.reduce((sum, d) => sum + parseInt(d.ordersCount), 0);

  // Last 7 days: filter by date
  const last7Days = filterByDateRange(analytics?.salesByDay, 7);
  // Sort by date ascending for chart
  const sorted7Days = [...last7Days].sort((a, b) => {
    const da = new Date(`${a.date}T00:00:00Z`);
    const db = new Date(`${b.date}T00:00:00Z`);
    return da - db;
  });
  const chartLabels = sorted7Days.map(d => {
    const dt = new Date(`${d.date}T00:00:00Z`);
    const localDate = new Date(dt.getTime() + dt.getTimezoneOffset() * 60000 * -1);
    return localDate.toLocaleDateString('en-CA');
  });
  const chartData = sorted7Days.map(d => parseFloat(d.totalSales));

  return (
    <div className="min-h-screen bg-gray-50 px-0 pb-5 pt-2 sm:px-6 sm:pb-6 sm:pt-3">
      <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <div className="w-full max-w-sm">
          <label className="mb-2 block text-sm font-medium text-gray-700">Branch</label>
          <Select
            classNamePrefix="react-select"
            styles={branchSelectStyles}
            options={branchOptions}
            value={branchOptions.find((option) => option.value === selectedBranchId) || branchOptions[0]}
            onChange={(option) => setSelectedBranchId(option?.value || "")}
            isSearchable={false}
          />
        </div>
      </div>

      {/* Row 1: KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {/* Today's Gross Sales */}
        <div className="rounded-lg border-l-4 border-blue-500 bg-gradient-to-br from-blue-50 via-white to-white p-4 shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Today's Gross Sales</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(today.totalSales)}</p>
              <p className="text-xs text-gray-400 mt-1">{today.ordersCount} transactions</p>
            </div>
            <ShoppingCart className="w-8 h-8 text-blue-500 opacity-20" />
          </div>
        </div>

        {/* Monthly Gross Sales */}
        <div className="rounded-lg border-l-4 border-green-500 bg-gradient-to-br from-green-50 via-white to-white p-4 shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">This Month's Gross Sales</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalSalesMonth)}</p>
              <p className="text-xs text-gray-400 mt-1">{totalOrdersMonth} orders</p>
            </div>
            <BarChart3 className="w-8 h-8 text-green-500 opacity-20" />
          </div>
        </div>

        {/* Today's Total Expenses */}
        <div className="rounded-lg border-l-4 border-amber-500 bg-gradient-to-br from-amber-50 via-white to-white p-4 shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Today's Total Expenses</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(todayExpenseTotal)}</p>
              <p className="text-xs text-gray-400 mt-1">{todayExpenseCount} entries</p>
              <Link to="/admin/expenses" className="text-xs text-blue-600 hover:underline mt-1 block">
                View full expenses →
              </Link>
            </div>
            <Wallet className="w-8 h-8 text-amber-500 opacity-20" />
          </div>
        </div>

        {/* Low Stock Alert */}
        <div className="rounded-lg border-l-4 border-red-500 bg-gradient-to-br from-red-50 via-white to-white p-4 shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Low Stock</p>
              <p className="text-2xl font-bold text-gray-900">{totalLowStockCount}</p>
              <Link to="/admin/inventory" className="text-xs text-blue-600 hover:underline mt-1 block">
                View inventory →
              </Link>
            </div>
            <AlertCircle className="w-8 h-8 text-red-500 opacity-20" />
          </div>
        </div>
      </div>

      {/* Row 2: Charts & Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Sales Trend Chart */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Sales Trend (Last 7 Days)</h2>
            <Link to="/admin/analytics" className="text-xs text-blue-600 hover:underline">
              View full →
            </Link>
          </div>
          <div className="h-60">
            <Line
              data={{
                labels: chartLabels,
                datasets: [
                  {
                    label: 'Sales',
                    data: chartData,
                    borderColor: '#6366F1',
                    backgroundColor: 'rgba(99,102,241,0.08)',
                    pointBackgroundColor: '#6366F1',
                    pointRadius: 4,
                    tension: 0.4,
                  },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { display: false } },
                scales: {
                  y: {
                    beginAtZero: true,
                    ticks: { callback: v => `₱${v}` },
                  },
                },
              }}
            />
          </div>
        </div>

        {/* Top Products */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Top Products</h2>
            <Link to="/admin/analytics" className="text-xs text-blue-600 hover:underline">
              View all →
            </Link>
          </div>
          <div className="space-y-3">
            {(analytics?.topProducts || []).slice(0, 5).map((product, idx) => (
              <div key={idx} className="flex items-center justify-between pb-3 border-b">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    {product.product || `Product ${product.product_id}`}
                  </p>
                  <p className="text-xs text-gray-500">{product.totalQty} units sold</p>
                </div>
                <div className="text-sm font-semibold text-gray-900">{product.totalQty}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Row 3: Pending Requests & Low Stock */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pending Pre-Release Requests */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Clock className="w-5 h-5 text-orange-500" />
              Pending Pre-Release ({preReleaseRequests.length})
            </h2>
            <Link to="/admin/pre-release-tracker" className="text-xs text-blue-600 hover:underline">
              View all →
            </Link>
          </div>
          {preReleaseRequests.length === 0 ? (
            <p className="text-sm text-gray-500 py-4">No pending requests</p>
          ) : (
            <div className="space-y-3">
              {preReleaseRequests.map((pr) => (
                <div key={pr.id} className="flex items-center justify-between pb-3 border-b last:border-0">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{pr.branch?.name}</p>
                    <p className="text-xs text-gray-500">{pr.items?.length || 0} items</p>
                  </div>
                  <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs font-semibold rounded">
                    {pr.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Low Stock Items */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-500" />
              Low Stock Items ({totalLowStockCount})
            </h2>
            <Link to="/admin/inventory" className="text-xs text-blue-600 hover:underline">
              View all →
            </Link>
          </div>
          {lowStockItems.length === 0 ? (
            <p className="text-sm text-gray-500 py-4">All items well stocked</p>
          ) : (
            <div className="space-y-3">
              {lowStockItems.map((item) => (
                <div key={item.id} className="flex items-center justify-between pb-3 border-b last:border-0">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">
                      {item.product_name || item.product?.name || item.name || `Product ${item.id}`}
                    </p>
                    <p className="text-xs text-gray-500">
                      {item.branch_name || item.branch?.name || 'Admin Warehouse'}
                    </p>
                  </div>
                  <span className={`px-2 py-1 text-xs font-semibold rounded ${item.quantity <= 3
                    ? 'bg-red-100 text-red-800'
                    : 'bg-yellow-100 text-yellow-800'
                    }`}>
                    {item.quantity} left
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
